import { Wallet } from "lucide-react";
import { formatCurrency } from "@veorest/utils";
import { formatShortDateTime } from "../../lib/partnerReports";
import { walletTxTypeKey } from "../../lib/partnerWallets";
import { usePartnerWalletV2 } from "../../hooks/usePartnerWallets";
import { useI18n } from "../../i18n";

/** Wallet + recent charges with settle (collection close-out for clients). */
export function WalletSettlementsCard() {
  const { t, locale } = useI18n();
  const query = usePartnerWalletV2();

  const wallet = query.data?.wallet;
  const transactions = query.data?.transactions ?? [];
  const money = (v: number) => formatCurrency(v, "IQD", locale);
  const signed = (v: number) =>
    `${v > 0 ? "+" : v < 0 ? "−" : ""}${money(Math.abs(v))}`;
  const balance = wallet?.balance ?? 0;
  const limit = wallet?.negative_credit_limit ?? 0;
  const blocked = wallet != null && balance < limit;

  return (
    <section className="menu-panel">
      <div className="menu-panel__body space-y-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[var(--accent-light)] text-[var(--accent)]">
            <Wallet className="h-4 w-4" />
          </span>
          <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
            {t("walletV2.title")}
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-3">
            <p className="text-xs font-semibold text-[var(--text-muted)]">
              {t("walletV2.balanceLabel")}
            </p>
            <p
              className={`mt-1 text-xl font-black tabular ${
                blocked ? "text-red-600" : "text-[var(--text-primary)]"
              }`}
            >
              {wallet ? money(balance) : "—"}
            </p>
          </div>
          <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-3">
            <p className="text-xs font-semibold text-[var(--text-muted)]">
              {t("walletV2.creditLimit")}
            </p>
            <p className="mt-1 text-xl font-black tabular text-[var(--text-primary)]">
              {wallet ? money(limit) : "—"}
            </p>
          </div>
        </div>

        <h4 className="text-xs font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
          {t("walletV2.transactionsTitle")}
        </h4>
        {query.isLoading ? (
          <div className="h-16 rounded-xl skeleton" />
        ) : transactions.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">{t("walletV2.empty")}</p>
        ) : (
          <ul className="space-y-2">
            {transactions.slice(0, 6).map((tx) => (
              <li
                key={tx.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--border-subtle)] px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[var(--text-primary)]">
                    {t(walletTxTypeKey(tx.type))}
                  </p>
                  <p className="truncate text-xs text-[var(--text-muted)]">
                    {formatShortDateTime(tx.created_at)}
                    {tx.description ? ` · ${tx.description}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`tabular text-sm font-bold ${
                      tx.amount < 0 ? "text-red-600" : "text-[var(--accent)]"
                    }`}
                  >
                    {signed(tx.amount)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-[var(--text-muted)]">{t("walletV2.feeOnlyNote")}</p>
      </div>
    </section>
  );
}
